/**
 * Product Description Formatter
 *
 * Finds ALL "Label: Value" patterns in product descriptions
 * and formats them as a two-column layout.
 * Preserves HTML formatting including inline styles.
 */
(function() {
  'use strict';

  // Maximum label length to avoid false positives with sentences containing colons
  const MAX_LABEL_LENGTH = 40;

  /**
   * Splits HTML content at the first colon, preserving all HTML structure
   * Uses TreeWalker and Range APIs to maintain DOM integrity
   * @param {string} html - HTML string to split
   * @returns {Object|null} - {labelHtml, valueHtml} or null if no valid split
   */
  function splitHtmlAtColon(html) {
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Get plain text to find colon position and validate pattern
    const textContent = temp.textContent || temp.innerText || '';
    const colonTextIndex = textContent.indexOf(':');

    // Must have a colon with text before and after
    if (colonTextIndex <= 0 || colonTextIndex >= textContent.length - 1) {
      return null;
    }

    const beforeColon = textContent.substring(0, colonTextIndex).trim();
    const afterColon = textContent.substring(colonTextIndex + 1).trim();

    // Validate: must have content on both sides
    if (!beforeColon || !afterColon) {
      return null;
    }

    // Heuristic: labels are typically short (avoid sentences with colons)
    if (beforeColon.length > MAX_LABEL_LENGTH) {
      return null;
    }

    // Heuristic: labels shouldn't contain sentence-ending punctuation
    if (/[.!?]/.test(beforeColon)) {
      return null;
    }

    // Use TreeWalker to find the colon's exact position in the DOM
    const walker = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT, null, false);
    let charCount = 0;
    let colonNode = null;
    let colonNodeOffset = 0;

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const nodeText = node.textContent;

      for (let i = 0; i < nodeText.length; i++) {
        if (charCount === colonTextIndex) {
          colonNode = node;
          colonNodeOffset = i;
          break;
        }
        charCount++;
      }

      if (colonNode) break;
    }

    if (!colonNode) return null;

    // Use Range API to extract label and value while preserving HTML
    try {
      const labelRange = document.createRange();
      labelRange.setStartBefore(temp.firstChild);
      labelRange.setEnd(colonNode, colonNodeOffset);

      const valueRange = document.createRange();
      valueRange.setStart(colonNode, colonNodeOffset + 1);
      if (temp.lastChild) {
        valueRange.setEndAfter(temp.lastChild);
      } else {
        valueRange.setEnd(colonNode, colonNode.textContent.length);
      }

      // Clone the contents preserving all HTML structure
      const labelFragment = labelRange.cloneContents();
      const valueFragment = valueRange.cloneContents();

      // Convert fragments to HTML strings
      const labelDiv = document.createElement('div');
      labelDiv.appendChild(labelFragment);

      const valueDiv = document.createElement('div');
      valueDiv.appendChild(valueFragment);

      return {
        labelHtml: labelDiv.innerHTML.trim(),
        valueHtml: valueDiv.innerHTML.trim()
      };
    } catch (e) {
      // Range API failed, fall back to null
      return null;
    }
  }

  /**
   * Formats product description by finding and styling "Label: Value" lines
   * Preserves all HTML formatting including inline styles
   * @param {HTMLElement} container - The description container element
   */
  function formatProductDescription(container) {
    if (!container || container.dataset.descriptionFormatted === 'true') {
      return;
    }

    const html = container.innerHTML;

    // Use a unique marker to split by line breaks while preserving HTML
    const MARKER = '\u0000SPLIT\u0000';
    const normalizedHtml = html
      .replace(/<br\s*\/?>/gi, MARKER)
      .replace(/<\/p>\s*<p[^>]*>/gi, MARKER);

    // Split into segments
    const segments = normalizedHtml.split(MARKER);
    const processedSegments = [];
    let hasSpecRows = false;

    for (const segment of segments) {
      // Clean up segment - remove wrapping p tags but keep inner content
      let cleanSegment = segment.trim()
        .replace(/^<p[^>]*>/i, '')
        .replace(/<\/p>$/i, '')
        .trim();

      if (!cleanSegment) continue;

      // Try to split at colon while preserving HTML
      const splitResult = splitHtmlAtColon(cleanSegment);

      if (splitResult) {
        processedSegments.push({
          type: 'spec',
          labelHtml: splitResult.labelHtml,
          valueHtml: splitResult.valueHtml
        });
        hasSpecRows = true;
      } else {
        processedSegments.push({
          type: 'text',
          html: cleanSegment
        });
      }
    }

    if (!hasSpecRows) {
      return;
    }

    // Build new HTML with preserved formatting
    const contentHtml = processedSegments
      .map(item => {
        if (item.type === 'spec') {
          return '<div class="description-spec-row"><span class="description-spec-label">' + item.labelHtml + ':</span><span class="description-spec-value">' + item.valueHtml + '</span></div>';
        } else {
          return '<p class="description-plain-text">' + item.html + '</p>';
        }
      })
      .join('');

    container.innerHTML = '<div class="description-spec-container">' + contentHtml + '</div>';
    container.dataset.descriptionFormatted = 'true';
  }

  /**
   * Initialize the formatter on all product descriptions
   */
  function init() {
    // Target the innermost RTE containers where descriptions are rendered
    const descriptionContainers = document.querySelectorAll(
      '.x-block-description .truncate-inner, .x-block-description .rte > .rte:not(.truncate-inner)'
    );

    // Also try broader selector if specific one fails
    if (descriptionContainers.length === 0) {
      const fallbackContainers = document.querySelectorAll('.x-block-description .rte');
      fallbackContainers.forEach(formatProductDescription);
    } else {
      descriptionContainers.forEach(formatProductDescription);
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run when Alpine.js components initialize (for dynamic content)
  document.addEventListener('alpine:initialized', function() {
    init();
  });

  // Expose for manual triggering if needed
  window.formatProductDescription = formatProductDescription;
  window.initProductDescriptionFormatter = init;
})();
